import classNames from 'classnames';
import React, { ComponentProps } from 'react';
import { t } from 'i18next';
import { AccentBgProvider } from '../accent-bg-context/accent-bg-context';
import { Input } from '../input/input';
import { OrderButton } from '../order-button/order-button';
import { Select, SelectOption } from '../select/select';
import { Tag } from '../tag/tag';
import { Typography, TypographyColor } from '../typography/typography';
import { notNil } from '../../core/helpers/is-nil';
import { IconVariant } from '../icon/icon-data';
import { Tooltip } from '../tooltip/tooltip';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { TagSkeleton } from '../tag/tag-skeleton';
import { useHorizontalOverflowHidden } from '../../modules/article-preview/use-horizontal-overflow-hidden';
import useDebounce from '../../core/hooks/use-debounce';
import { ExpandedSection } from './components/expanded-section';
import { CartDecyphering } from './components/cart-decyphering';
import styles from './add-to-cart-section.module.scss';

type SectionLoadingState = {
  state: 'loading';
};

const isSectionLoadingState = (val: unknown | SectionLoadingState): val is SectionLoadingState => {
  return notNil(val) && (val as SectionLoadingState).state === 'loading';
};

type OrderSummary = {
  secondaryText: string;
  formattedAmount: string;
  formattedPrice: string;
  priceUnit: string;
};

const isOrderSummary = (val: OrderSummary | SectionLoadingState | undefined): val is OrderSummary => {
  return notNil(val) && (val as SectionLoadingState).state !== 'loading';
};

type Props = {
  state: 'darkBg' | 'lightBg' | 'lightBgExpanded';
  disabledInput?: boolean;
  disabled?: boolean;
  titleText: string | undefined;
  explainer?: Pick<ComponentProps<typeof Tooltip>, 'title' | 'text' | 'link'>;
  availability:
    | {
        tagText: string;
        filled: boolean;
      }
    | SectionLoadingState;
  weightPlaceholderText: string;
  weightValue: string;
  onWeightChange: (weightValue: string) => void;
  selectPlaceholderText: string;
  selectedOption: string | undefined;
  onSelectOption: (value: string) => void;
  selectOptions: SelectOption[];
  orderSummary: OrderSummary | SectionLoadingState | undefined;
  actionButton: {
    text: string;
    icon: IconVariant;
    onClick: () => void;
  };
  warningText?: string;
  articleUnit: string;
  moveOut?: boolean;
} & ComponentProps<typeof ExpandedSection>;

export const AddToCartSection: React.FC<Props> = ({
  state,
  disabledInput = false,
  disabled = false,
  titleText,
  explainer,
  availability,
  weightPlaceholderText,
  weightValue,
  onWeightChange,
  selectPlaceholderText,
  selectedOption,
  onSelectOption,
  selectOptions,
  orderSummary,
  articleNumber,
  series,
  hexColor,
  warningText,
  articleUnit,
  actionButton,
  moveOut = false,
}) => {
  const showAccentBg = state === 'darkBg';
  let textColor: TypographyColor = 'black';
  if (showAccentBg) textColor = 'white';

  const isLoadingAvailability = isSectionLoadingState(availability);

  const isLoadingOrderSummary = isSectionLoadingState(orderSummary);
  const hasOrderSummary = isOrderSummary(orderSummary);

  const MOVE_OUT_DURATION_MS = 200;
  const debouncedMoveOut = useDebounce(moveOut, MOVE_OUT_DURATION_MS + 100);
  useHorizontalOverflowHidden(debouncedMoveOut || moveOut);

  const hasSelectOptions = selectOptions.length > 0;

  return (
    <AccentBgProvider isAccentBackground={showAccentBg}>
      <div
        className={classNames(styles.root, {
          [styles.lightBg]: state === 'lightBg',
          [styles.lightBgExpanded]: state === 'lightBgExpanded',
          [styles.moveOut]: moveOut,
        })}
      >
        <ExpandedSection articleNumber={articleNumber} hexColor={hexColor} series={series} />
        <div className={styles.topContainer}>
          {isLoadingAvailability ? <TagSkeleton /> : <Tag filled={availability.filled}>{availability.tagText}</Tag>}
          <div className={styles.mainContent}>
            <div className={classNames(styles.formContainer, { [styles.disabled]: disabledInput })}>
              {notNil(titleText) ? (
                <div className={styles.titleContainer}>
                  <Typography color={textColor} variant="headline-3">
                    {titleText}
                  </Typography>
                  {notNil(explainer) ? (
                    <Tooltip {...explainer} position="topRight" tabIndex={moveOut ? -1 : undefined} />
                  ) : null}
                </div>
              ) : null}
              {!disabledInput ? (
                <>
                  <Input
                    inputMode="numeric"
                    placeholder={weightPlaceholderText}
                    tabIndex={moveOut ? -1 : undefined}
                    type="text"
                    unit={articleUnit}
                    value={weightValue}
                    inlineNotification={
                      notNil(warningText)
                        ? {
                            type: 'warning',
                            message: warningText,
                          }
                        : undefined
                    }
                    onChange={(e) => {
                      onWeightChange(e.target.value);
                    }}
                  />
                  <Select
                    ariaLabel={selectPlaceholderText}
                    disabled={isLoadingAvailability}
                    placeholder={selectPlaceholderText}
                    tabIndex={moveOut ? -1 : undefined}
                    value={selectedOption}
                    options={
                      hasSelectOptions
                        ? selectOptions
                        : [{ label: t('article.containerSize.subjectToAvailability'), value: 'subjectToAvailability' }]
                    }
                    onChange={(newVal) => (hasSelectOptions ? onSelectOption(newVal) : null)}
                  />
                </>
              ) : null}
            </div>
            {hasOrderSummary ? (
              <CartDecyphering
                articleUnit={articleUnit}
                formattedAmount={orderSummary.formattedAmount}
                formattedPrice={orderSummary.formattedPrice}
                priceUnit={orderSummary.priceUnit}
                secondaryText={orderSummary.secondaryText}
                textColor={textColor}
              />
            ) : null}
            {isLoadingOrderSummary ? <LoadingSpinner variant="line" /> : null}
          </div>
        </div>
        <OrderButton disabled={disabled || moveOut} icon={actionButton.icon} onClick={actionButton.onClick}>
          {actionButton.text}
        </OrderButton>
      </div>
    </AccentBgProvider>
  );
};
